import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import AgentLayout from "../components/layout/AgentLayout";
import PageHeader from "../components/common/PageHeader";
import { DASHBOARD_CRUMB } from "../components/common/Breadcrumb";
import { agentApi } from "../lib/apiClient";
import { showApiError } from "../lib/alerts";
import PageLoader from "../components/common/PageLoader";
import { BRAND } from "../lib/brand";

function formatDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function displayValue(value, fallback = "—") {
  if (value == null || String(value).trim() === "") return fallback;
  return value;
}

function Field({ label, value }) {
  return (
    <div className="agent-voucher-field">
      <div className="agent-voucher-label">{label}</div>
      <div className="agent-voucher-value">{displayValue(value)}</div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="agent-voucher-section">
      <h2 className="agent-voucher-section-title">{title}</h2>
      {children}
    </section>
  );
}

function FlightBlock({ title, airline, flightNo, from, to, pnr, date, time }) {
  return (
    <article className="agent-voucher-flight">
      <div className="agent-voucher-flight-head">
        <span className="agent-voucher-flight-leg">{title}</span>
        <span className="agent-voucher-flight-meta">
          {displayValue(airline)}
          {flightNo ? ` · ${flightNo}` : ""}
        </span>
      </div>
      <div className="agent-voucher-route">
        <div className="agent-voucher-airport">
          <div className="agent-voucher-airport-kicker">From</div>
          <div className="agent-voucher-airport-name">{displayValue(from)}</div>
        </div>
        <div className="agent-voucher-route-line" aria-hidden="true">
          <span />
        </div>
        <div className="agent-voucher-airport agent-voucher-airport--end">
          <div className="agent-voucher-airport-kicker">To</div>
          <div className="agent-voucher-airport-name">{displayValue(to)}</div>
        </div>
      </div>
      <div className="agent-voucher-flight-foot">
        <span>
          <strong>PNR</strong> {displayValue(pnr)}
        </span>
        {date ? (
          <span>
            <strong>Date</strong> {date}
            {time ? ` · ${time}` : ""}
          </span>
        ) : null}
      </div>
    </article>
  );
}

function printVoucher(bookingRef) {
  const previousTitle = document.title;
  document.title = `Travel-Voucher-${bookingRef || "Way2Umrah"}`;
  const restore = () => {
    document.title = previousTitle;
    window.removeEventListener("afterprint", restore);
  };
  window.addEventListener("afterprint", restore);
  window.print();
}

export default function BookingVoucherPage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.body.classList.add("agent-voucher-open");
    return () => document.body.classList.remove("agent-voucher-open");
  }, []);

  useEffect(() => {
    let alive = true;
    async function load() {
      try {
        const payload = await agentApi.get(`/travel-partner/bookings/${id}/travel`);
        if (!alive) return;
        setData(payload?.data || payload || {});
      } catch (fetchError) {
        if (alive) showApiError(fetchError, "LOAD_VOUCHER");
      } finally {
        if (alive) setLoading(false);
      }
    }
    load();
    return () => {
      alive = false;
    };
  }, [id]);

  const booking = data?.booking || {};
  const travel = data?.operation || {};
  const pilgrims = Array.isArray(data?.pilgrims) ? data.pilgrims : [];
  const bookingRef = booking.booking_reference || `Booking ${id}`;
  const issuedOn = formatDate(
    travel.voucher_published_at || travel.voucher_generated_at || travel.published_at || new Date()
  );
  const hasReturn =
    travel.return_flight_number || travel.return_airline_name || travel.return_departure_airport;
  const onwardDate = formatDate(travel.flight_departure_date || travel.departure_datetime || booking.departure_date);
  const returnDate = formatDate(travel.return_departure_date || travel.return_departure_datetime || booking.return_date);

  return (
    <AgentLayout>
      <div className="container-fluid py-24 agent-voucher-page">
        <PageHeader
          className="agent-voucher-toolbar"
          eyebrow="Voucher"
          title={bookingRef}
          items={[
            DASHBOARD_CRUMB,
            { label: "Bookings", href: "/bookings" },
            { label: "Booking Details", href: `/bookings/${id}` },
            { label: "Voucher" },
          ]}
          backTo={`/bookings/${id}`}
          actions={
            <div className="d-flex gap-2">
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={() => printVoucher(booking.booking_reference)}
              >
                Print
              </button>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                title="Opens a clean print layout. Choose Save as PDF to download."
                onClick={() => printVoucher(booking.booking_reference)}
              >
                Download
              </button>
            </div>
          }
        />

        {loading ? <PageLoader message="Loading voucher..." /> : null}

        {!loading && data ? (
          <article className="agent-voucher-sheet" id="agent-voucher-sheet">
            <header className="agent-voucher-letterhead">
              <div className="agent-voucher-brand">
                <img src={BRAND.logo} alt={BRAND.name} style={{ height: "64px" }} className="agent-voucher-logo" />
                {/* <div>
                  <div className="agent-voucher-brand-name">{BRAND.name}</div>
                  <div className="agent-voucher-brand-tag">Authorized travel voucher</div>
                </div> */}
              </div>
              <div className="agent-voucher-docmark">
                <div className="agent-voucher-docmark-title">Travel Voucher</div>
                <div className="agent-voucher-docmark-ref">{bookingRef}</div>
                <div className="agent-voucher-docmark-meta">Issued {issuedOn}</div>
              </div>
            </header>

            <div className="agent-voucher-meta">
              {/* <Field label="Booking" value={booking.booking_reference} /> */}
              <Field label="Package" value={booking.package_name} />
              <Field label="Customer" value={booking.customer_name} />
              <Field label="Departure" value={formatDate(booking.departure_date || booking.booking_date)} />
              {booking.return_date ? <Field label="Return" value={formatDate(booking.return_date)} /> : null}
              <Field label="Pilgrims" value={pilgrims.length || booking.booking_pilgrim_count || "—"} />
            </div>

            <Section>
              <h6>Flights</h6>
              <div className={`agent-voucher-flights${hasReturn ? " agent-voucher-flights--split" : ""}`}>
                <FlightBlock
                  title="Onward"
                  airline={travel.flight_airline_name}
                  //flightNo={travel.flight_number}
                  from={travel.departure_airport}
                  to={travel.arrival_airport}
                  pnr={travel.pnr}
                  date={onwardDate}
                  time={travel.flight_departure_time || travel.departure_time}
                />
                {hasReturn ? (
                  <FlightBlock
                    title="Return"
                    airline={travel.return_airline_name || travel.flight_airline_name}
                   // flightNo={travel.return_flight_number}
                    from={travel.return_departure_airport}
                    to={travel.return_arrival_airport}
                    pnr={travel.return_pnr || travel.pnr}
                    date={returnDate}
                    time={travel.return_departure_time}
                  />
                ) : null}
              </div>
            </Section>

            <Section>
              <h6>Hotels</h6>
              <div className="agent-voucher-hotels">
                <article className="agent-voucher-hotel">
                  <div className="agent-voucher-hotel-city">Makkah</div>
                  <div className="agent-voucher-hotel-name">{displayValue(travel.makkah_hotel_name)}</div>
                  <div className="agent-voucher-hotel-stay">
                    {formatDate(travel.makkah_check_in) || "Check-in not set"}
                    {" – "}
                    {formatDate(travel.makkah_check_out) || "Check-out not set"}
                  </div>
                  <div className="agent-voucher-meta agent-voucher-meta--3 mt-2">
                    <Field label="Meal plan" value={travel.makkah_meal_plan || travel.meal_plan} />
                    <Field label="Occupancy type" value={travel.makkah_occupancy_type || travel.occupancy_type} />
                    <Field label="Confirmation" value={travel.makkah_confirmation_number || travel.hotel_confirmation_number} />
                  </div>
                </article>
                <article className="agent-voucher-hotel">
                  <div className="agent-voucher-hotel-city">Madinah</div>
                  <div className="agent-voucher-hotel-name">{displayValue(travel.madinah_hotel_name)}</div>
                  <div className="agent-voucher-hotel-stay">
                    {formatDate(travel.madinah_check_in) || "Check-in not set"}
                    {" – "}
                    {formatDate(travel.madinah_check_out) || "Check-out not set"}
                  </div>
                  <div className="agent-voucher-meta agent-voucher-meta--3 mt-2">
                    <Field label="Meal plan" value={travel.madinah_meal_plan || travel.meal_plan} />
                    <Field label="Occupancy type" value={travel.madinah_occupancy_type || travel.occupancy_type} />
                    <Field label="Confirmation" value={travel.madinah_confirmation_number} />
                  </div>
                </article>
              </div>
            </Section>

            <Section>
              <h6>Pilgrims</h6>
              {pilgrims.length > 0 ? (
                <table className="agent-voucher-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Name</th>
                      <th>Passport</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pilgrims.map((pilgrim, index) => (
                      <tr key={pilgrim.passenger_id || index}>
                        <td>{index + 1}</td>
                        <td>{displayValue(pilgrim.passenger_name)}</td>
                        <td>{displayValue(pilgrim.uppd_passport_no, "Not provided")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="agent-voucher-empty">No pilgrims listed yet.</p>
              )}
            </Section>

            <footer className="agent-voucher-footer text-center">
              <p>This voucher is for authorized agent use. Travel details are as published at the time of issue.</p>
              <p>
                {BRAND.name} · {bookingRef} · {issuedOn}
              </p>
            </footer>
          </article>
        ) : null}
      </div>
    </AgentLayout>
  );
}
